'use client';

import { c as _c } from "react/compiler-runtime";
import type { MergeabilityStatusResponse } from '@/lib/hooks/mergeability-status/query-data';
import type { PrV2SummaryResponse } from '@/lib/pr-v2/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { updatePrV2State } from './fetch-pr-v2';
import { recomputePrV2MergeabilityStatus, rollbackQueryData } from './mergeability-utils';
import { prV2DiscussionQueryKey, prV2SummaryQueryKey } from './query-keys';
import { patchInboxPullRequest } from '@/data/pull-requests/patch-inbox-pull-request';
import { clearPendingOptimisticMutation } from '@/data/pull-requests/pending-optimistic-mutations';
import { inboxV2MergedPullRequestsQueryKeyPrefix, inboxV2OpenPullRequestsQueryKeyPrefix } from '@/data/pull-requests/query-keys';
import { mergeabilityStatusQueryKey } from '@/lib/hooks/mergeability-status/keys';
export function usePrV2PublishAction(owner, repo, number) {
  const $ = _c(34);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== number || $[1] !== owner || $[2] !== repo) {
    t0 = prV2SummaryQueryKey(owner, repo, number);
    $[0] = number;
    $[1] = owner;
    $[2] = repo;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const summaryKey = t0;
  let t1;
  if ($[4] !== number || $[5] !== owner || $[6] !== repo) {
    t1 = prV2DiscussionQueryKey(owner, repo, number);
    $[4] = number;
    $[5] = owner;
    $[6] = repo;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const discussionKey = t1;
  let t2;
  if ($[8] !== number || $[9] !== owner || $[10] !== repo) {
    t2 = mergeabilityStatusQueryKey(owner, repo, number, "v2");
    $[8] = number;
    $[9] = owner;
    $[10] = repo;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const mergeabilityKey = t2;
  let t3;
  if ($[12] !== number || $[13] !== owner || $[14] !== repo) {
    t3 = async () => {
      const promise = updatePrV2State(owner, repo, number, {
        draft: false
      });
      toast.promise(promise, {
        loading: "Marking pull request ready for review...",
        success: "Pull request marked ready for review",
        error: _temp
      });
      return promise;
    };
    $[12] = number;
    $[13] = owner;
    $[14] = repo;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  let t5;
  if ($[16] !== mergeabilityKey || $[17] !== number || $[18] !== owner || $[19] !== queryClient || $[20] !== repo || $[21] !== summaryKey) {
    t4 = async () => {
      await Promise.all([queryClient.cancelQueries({
        queryKey: summaryKey
      }), queryClient.cancelQueries({
        queryKey: mergeabilityKey
      })]);
      const previousSummary = queryClient.getQueryData(summaryKey);
      const previousMergeability = queryClient.getQueryData(mergeabilityKey);
      queryClient.setQueryData(summaryKey, _temp2);
      queryClient.setQueryData(mergeabilityKey, _temp4);
      patchInboxPullRequest(queryClient, {
        owner,
        repo,
        number: Number(number)
      }, _temp5);
      return {
        previousSummary,
        previousMergeability
      };
    };
    t5 = (_error, _variables, context) => {
      clearPendingOptimisticMutation({
        owner,
        repo,
        number: Number(number)
      });
      if (!context) {
        return;
      }
      rollbackQueryData(queryClient, summaryKey, context.previousSummary);
      rollbackQueryData(queryClient, mergeabilityKey, context.previousMergeability);
    };
    $[16] = mergeabilityKey;
    $[17] = number;
    $[18] = owner;
    $[19] = queryClient;
    $[20] = repo;
    $[21] = summaryKey;
    $[22] = t4;
    $[23] = t5;
  } else {
    t4 = $[22];
    t5 = $[23];
  }
  let t6;
  if ($[24] !== discussionKey || $[25] !== mergeabilityKey || $[26] !== queryClient || $[27] !== summaryKey) {
    t6 = async () => {
      await Promise.all([queryClient.invalidateQueries({
        queryKey: summaryKey
      }), queryClient.invalidateQueries({
        queryKey: discussionKey
      }), queryClient.invalidateQueries({
        queryKey: mergeabilityKey
      }), queryClient.invalidateQueries({
        queryKey: inboxV2OpenPullRequestsQueryKeyPrefix()
      }), queryClient.invalidateQueries({
        queryKey: inboxV2MergedPullRequestsQueryKeyPrefix()
      })]);
    };
    $[24] = discussionKey;
    $[25] = mergeabilityKey;
    $[26] = queryClient;
    $[27] = summaryKey;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== t3 || $[30] !== t4 || $[31] !== t5 || $[32] !== t6) {
    t7 = {
      mutationFn: t3,
      onMutate: t4,
      onError: t5,
      onSettled: t6
    };
    $[29] = t3;
    $[30] = t4;
    $[31] = t5;
    $[32] = t6;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  return useMutation(t7);
}
function _temp5(pr) {
  return {
    ...pr,
    draft: false,
    updatedAt: new Date().toISOString()
  };
}
function _temp4(current_0) {
  return recomputePrV2MergeabilityStatus(current_0, _temp3);
}
function _temp3(status) {
  return {
    ...status,
    draft: false
  };
}
function _temp2(current) {
  return current ? {
    ...current,
    pullRequest: {
      ...current.pullRequest,
      draft: false
    }
  } : current;
}
function _temp(error) {
  return error instanceof Error ? error.message : "Failed to mark pull request ready for review";
}